/**
 * The membership's navigation model — the single source of truth for what
 * sections exist, what they're called, and which are group-scoped.
 *
 * This lived in MembershipHome and was handed to MembershipShell as a prop, so
 * "what's in the menu" was a per-page decision and the two pages could (and did)
 * disagree. The header owns it now; pages just say which section is open.
 */

export const TAB_ICON = {
  home: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.5 1.5 0 012.122 0L22.5 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75" />
    </svg>
  ),
  groups: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h6v6H4V5zm10 0h6v6h-6V5zM4 15h6v4H4v-4zm10 0h6v4h-6v-4z" />
    </svg>
  ),
  feed: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.5 1.5 0 012.122 0L22.5 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75" />
    </svg>
  ),
  members: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  ),
  leaderboard: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0V4zM7 5H4v2a3 3 0 003 3M17 5h3v2a3 3 0 01-3 3" />
    </svg>
  ),
  rewards: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 12v9H4v-9M2 7h20v5H2V7zm10 0v14M12 7S9 7 8 5.5 9 3 10 3.5 12 7 12 7zm0 0s3 0 4-1.5S15 3 14 3.5 12 7 12 7z" />
    </svg>
  ),
  events: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
    </svg>
  ),
  courses: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  ),
  map: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  ),
  memberchat: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 20l1.3-3.9A7.96 7.96 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
};

export const NAV_TABS = [
  // The member dashboard. Opt-in (Settings → Membership Menus → Home): unlike
  // the other tabs it is HIDDEN unless community.menus.home is explicitly 0 —
  // see homeEnabled() below.
  { key: "home", label: "Home", icon: TAB_ICON.home },
  { key: "courses", label: "Courses", icon: TAB_ICON.courses },
  { key: "feed", label: "Feed", icon: null },
  // Labelled "Community" in the nav: the section is the community of groups,
  // not a single group.
  { key: "groups", label: "Community", icon: TAB_ICON.groups },
  { key: "events", label: "Events", icon: TAB_ICON.events },
  { key: "members", label: "Members", icon: TAB_ICON.members },
  { key: "map", label: "Map", icon: TAB_ICON.map },
  { key: "leaderboard", label: "Leaderboard", icon: TAB_ICON.leaderboard },
  { key: "rewards", label: "Rewards", icon: TAB_ICON.rewards },
  // Members Chat — the membership-wide messaging center (full page). Opens the
  // chat inbox + contact picker; the per-member "Chat" button on the Members
  // list was retired in favour of this section.
  { key: "memberchat", label: "Members Chat", icon: TAB_ICON.memberchat },
];

// Primary tabs shown in the community-home navbar. "feed" is intentionally
// excluded here — the feed is group-scoped, so it only appears inside a group
// (MembershipShell's in-group header). "groups"/"members"/"events" switch the
// community view in place; the group-scoped tabs open a group on that page.
export const COMMUNITY_NAV_KEYS = ["home", "courses", "groups", "events", "members", "map"];

// Overflow tabs collapsed into the community-home "More ▾" dropdown — mirrors the
// in-group header's More menu so the navbar is the same everywhere.
export const COMMUNITY_MORE_KEYS = ["leaderboard", "rewards", "memberchat"];

export const PENDING_ICON_PATH = "M6 3.75h12M6 20.25h12M7.5 3.75v2.379a3 3 0 00.879 2.121L12 12l3.621-3.621a3 3 0 00.879-2.121V3.75M7.5 20.25v-2.379a3 3 0 01.879-2.121L12 12l3.621 3.621a3 3 0 01.879 2.121v2.379";

export const NAV_PENDING_ITEM = {
  key: "pendingpost",
  label: "Pending Posts",
  icon: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d={PENDING_ICON_PATH} />
    </svg>
  ),
};

export const NAV_ABOUT_ITEM = {
  key: "about",
  label: "About",
  icon: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

// Group-scoped sections belong to a specific group and carry ?gid in the URL.
// Only the Feed (and its sub-pages) is group-related; Courses, Events, Members,
// Map, Leaderboard and Rewards are community-wide membership pages.
export const GROUP_SCOPED_PAGES = ["feed", "about", "pendingpost"];

/**
 * Every slug that is a real section of /membership/:token/:section.
 *
 * DERIVED from the nav definitions rather than typed out again, so a new tab
 * becomes a valid URL the moment it is added to NAV_TABS. A hand-kept copy would
 * drift, and it fails in the worse direction: a working page starts 404ing
 * because someone added a tab and missed the list.
 *
 * Used by the route guard in App.js. Without it an unknown slug matched
 * `:section` anyway and fell through MembershipHome's render chain to the
 * community group list, so a typo'd URL rendered as a real page.
 */
/**
 * Account pages — My Profile, Change Password, My Plans as full pages. Only the
 * Spotlight template links to them (every other template keeps these as
 * popups), but they are real sections so a refresh or a shared link holds.
 */
/**
 * "My earnings" — what a member has made from the links they share.
 *
 * An account page like the three above, with one difference: those have
 * popups that work perfectly well, so only Spotlight opens them as pages.
 * This one is a page in EVERY template, because the popup is what it was
 * before and the popup was the problem — eighty sales across thirty products
 * is a table, and a table does not fit in 32rem. The sharing LINKS keep their
 * popup; they are the one-off.
 *
 * Reached from the account menu, never from the nav: most members of most
 * memberships will never be affiliates, and a permanently dead tab is worse
 * than a feature being one menu deep.
 */
export const EARNINGS_PAGE = "earnings";

export const ACCOUNT_PAGES = ["profile", "password", "plans", EARNINGS_PAGE];

/** Spotlight's search results page (/search?q=…), opened from its top bar. */
export const SEARCH_PAGE = "search";

/**
 * A creator's custom menu page — /page?id=… (Settings → Membership Menus →
 * Custom menus). One section for all of them; the id picks the page.
 */
export const CUSTOM_PAGE = "page";

const CUSTOM_TAB_ICON = (
  <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8.25V18a2.25 2.25 0 0 0 2.25 2.25h13.5A2.25 2.25 0 0 0 21 18V8.25m-18 0V6a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 6v2.25m-18 0h18M5.25 6h.008v.008H5.25V6ZM7.5 6h.008v.008H7.5V6Zm2.25 0h.008v.008H9.75V6Z" />
  </svg>
);

/** Nav key for a custom menu. Keys look like "page:abc123". */
export const customKey = (id) => `${CUSTOM_PAGE}:${id}`;
export const isCustomKey = (key) => String(key || "").startsWith(`${CUSTOM_PAGE}:`);
export const customIdOf = (key) => String(key || "").slice(CUSTOM_PAGE.length + 1);

/** The creator's custom menus as nav tabs, in their order (after the standard ones). */
export const customTabs = (community) =>
  (Array.isArray(community?.custom_menus) ? community.custom_menus : [])
    .filter((m) => m && m.id && m.label)
    .map((m) => ({ key: customKey(m.id), label: m.label, icon: CUSTOM_TAB_ICON, custom: true }));

/**
 * Tabs in the creator's order (Settings → Membership Menus, drag and drop).
 * community.menu_order lists nav keys — standard ("courses") and custom
 * ("page:abc123"); tabs it doesn't name keep their default order after it.
 * No order = the default NAV_TABS order, custom menus last.
 */
export const orderTabs = (tabs, order) => {
  const list = Array.isArray(order) ? order : [];
  if (!list.length) return tabs;
  const rank = new Map(list.map((k, i) => [k, i]));
  return tabs
    .map((t, i) => [t, rank.has(t.key) ? rank.get(t.key) : list.length + i])
    .sort((a, b) => a[1] - b[1])
    .map(([t]) => t);
};

/**
 * Header templates: which tabs sit in the bar and which go under "More ▾".
 * The bar holds as many tabs as it always has (one per visible primary tab),
 * taken from the top of the order — so a custom menu placed after Home shows
 * in the bar, and with the default order the split is exactly as before.
 */
export const splitInline = (tabs, primaryKeys) => {
  const n = tabs.filter((t) => primaryKeys.includes(t.key)).length;
  return [tabs.slice(0, n), tabs.slice(n)];
};

/**
 * Where every sign-in lands: not a page of its own, it forwards to the
 * membership's Default page (landingSection) once the membership has loaded.
 * Kept apart from /home because /home is also the Home tab — a member who
 * clicks Home must get Home, whatever the default page is.
 */
export const START_PAGE = "start";

export const SECTION_SLUGS = [
  START_PAGE,
  ...NAV_TABS.map((t) => t.key),
  NAV_PENDING_ITEM.key,
  NAV_ABOUT_ITEM.key,
  ...ACCOUNT_PAGES,
  SEARCH_PAGE,
  CUSTOM_PAGE,
];

// Sections that belong to the MEMBERSHIP, not to any one group. These are what
// the nav offers everywhere.
export const MEMBERSHIP_PAGES = ["home", "courses", "groups", "events", "members", "map", "leaderboard", "rewards", "memberchat"];

/**
 * Is the Home dashboard switched on for this membership?
 *
 * Every other menu flag hides on 1 and shows when absent, because those menus
 * predate the flag. Home is the reverse: a bridge that predates it sends no
 * `home` key at all, and reading that as "on" would drop every member of every
 * membership onto a page their creator never enabled. Only an explicit 0 is on.
 */
export const homeEnabled = (menus) => Number(menus?.home) === 0;

/**
 * Where the logo and a fresh sign-in take a member: the creator's Default page
 * (Settings → Membership Menus). flexiapi sends it already resolved to a menu
 * that is shown — a nav key like "courses" or "page:abc123". An older API
 * without it keeps the old rule: the Home dashboard when on, else Courses.
 */
export const landingSection = (menus) => {
  const d = menus?.default_page;
  if (typeof d === "string" && d) return d;
  return homeEnabled(menus) ? "home" : "courses";
};

/** URL for a nav key — a custom menu ("page:<id>") opens /page?id=<id>. */
export const sectionPath = (token, key, ref) => {
  const p = new URLSearchParams();
  let section = key;
  if (isCustomKey(key)) {
    section = CUSTOM_PAGE;
    p.set("id", customIdOf(key));
  }
  if (ref) p.set("ref", ref);
  const qs = p.toString();
  return `/membership/${token}/${section}${qs ? `?${qs}` : ""}`;
};

/**
 * ---------------------------------------------------------------------------
 * Loading-screen icons
 * ---------------------------------------------------------------------------
 * The full-screen loader shows the icon of the page you are ON — the same mark
 * the nav uses for it — so a load reads as "Members is coming" rather than as a
 * generic spinner that could belong to anything.
 *
 * Nav tabs supply their own icons; the map below adds the pages that get a
 * loading screen without being nav tabs (a lesson, a checkout, an instructor).
 */
export const PAGE_ICON = {
  ...TAB_ICON,
  about: NAV_ABOUT_ITEM.icon,
  pendingpost: NAV_PENDING_ITEM.icon,
  // A lesson — play glyph inside the frame, because a lesson is a thing you
  // watch, while `courses` (the book) is the shelf you pick it from.
  lesson: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5.5h16a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1v-10a1 1 0 011-1zM8.5 21h7M12 17.5V21M10.5 9.2l3.6 2.3-3.6 2.3V9.2z" />
    </svg>
  ),
  checkout: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12l1.2 12.1a1 1 0 01-1 1.1H5.8a1 1 0 01-1-1.1L6 7zm3 0V5.5a3 3 0 016 0V7" />
    </svg>
  ),
  instructor: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 11.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM5 20a7 7 0 0114 0M17.5 6.2l2.2-1M17.5 6.2l2.2 1" />
    </svg>
  ),
  profile: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 12a4 4 0 100-8 4 4 0 000 8zM4.5 20a7.5 7.5 0 0115 0" />
    </svg>
  ),
  // A coin, not a wallet: the page is about what the sales earned, not about
  // an account holding it.
  earnings: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5v9m2.5-6.6c0-.9-1.1-1.6-2.5-1.6s-2.5.7-2.5 1.6c0 2.2 5 1.1 5 3.3 0 .9-1.1 1.6-2.5 1.6s-2.5-.7-2.5-1.6M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

// What a caller may say, and the icon key it means. Callers name the page in
// human words ("Members Chat", "Loading pending posts…", "Community") — this is
// what turns any of those into one of the keys above, so call sites never have
// to know the internal slugs.
const PAGE_ICON_ALIASES = {
  community: "groups",
  group: "groups",
  groupfeed: "feed",
  dashboard: "home",
  member: "members",
  memberslist: "members",
  chat: "memberchat",
  memberschat: "memberchat",
  messages: "memberchat",
  event: "events",
  reward: "rewards",
  points: "leaderboard",
  ranking: "leaderboard",
  course: "courses",
  coursearea: "courses",
  lessons: "lesson",
  pendingposts: "pendingpost",
  pending: "pendingpost",
  order: "checkout",
  payment: "checkout",
  thankyou: "checkout",
  instructors: "instructor",
  account: "profile",
};

/**
 * The icon for a page, from whatever the caller happens to call it — a section
 * key ("memberchat"), a display name ("Members Chat"), or a loader label
 * ("Loading pending posts…"). Returns `null` when nothing matches, which is a
 * real answer: the loader then falls back to the community logo.
 */
export function iconForPage(name) {
  if (!name) return null;
  const slug = String(name)
    .replace(/^\s*loading\s+/i, "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  if (!slug) return null;
  return PAGE_ICON[slug] || PAGE_ICON[PAGE_ICON_ALIASES[slug]] || null;
}
